import { useCallback, useMemo } from 'react';

import { type ScopedPermissions } from '@air/api/types';

import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { useUploadFoldersFromComputer } from '~/hooks/useUploadFoldersFromComputer';
import { getHeaderUploadMenuOptions } from '~/utils/dropdownMenuOptions/getHeaderUploadMenuOptions';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const LIBRARY_HEADER_UPLOAD_TRACK_LOCATION = 'header-bar-add-button';

export type UseLibraryHeaderUploadActionsParams = {
  permissions: ScopedPermissions | undefined;
};

export const useLibraryHeaderUploadActions = ({ permissions }: UseLibraryHeaderUploadActionsParams) => {
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({
    pickDestination: true,
    trackLocation: LIBRARY_HEADER_UPLOAD_TRACK_LOCATION,
  });
  const { uploadFoldersFromComputer } = useUploadFoldersFromComputer({
    pickDestination: true,
    trackLocation: LIBRARY_HEADER_UPLOAD_TRACK_LOCATION,
  });
  const { isStoragePlanRestricted, showStoragePlanRestrictionModal } = useStoragePlanRestriction();

  const canAddAssets = canCreateAsset(permissions);
  const canCreateBoards = canCreateBoard(permissions);

  const onUploadFilesFromComputer = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFilesFromComputer();
    }
  }, [isStoragePlanRestricted, showStoragePlanRestrictionModal, uploadFilesFromComputer]);

  const onUploadFoldersFromComputer = useCallback(() => {
    if (isStoragePlanRestricted) {
      showStoragePlanRestrictionModal();
    } else {
      uploadFoldersFromComputer();
    }
  }, [isStoragePlanRestricted, showStoragePlanRestrictionModal, uploadFoldersFromComputer]);

  return {
    onUploadFilesFromComputer: canAddAssets ? onUploadFilesFromComputer : undefined,
    onUploadFoldersFromComputer: canAddAssets && canCreateBoards ? onUploadFoldersFromComputer : undefined,
  };
};

export const useLibraryHeaderUploadMenuOptions = ({ permissions }: UseLibraryHeaderUploadActionsParams) => {
  const { onUploadFilesFromComputer, onUploadFoldersFromComputer } = useLibraryHeaderUploadActions({ permissions });

  const uploadMenuOptions = useMemo(
    () =>
      getHeaderUploadMenuOptions({
        // A library is not an import destination. Offering a source navigates to
        // `/l/.../import/<source>`, which has no page and 404s.
        exposeImportSources: [],
        onImportFrom: undefined,
        onUploadFilesFromComputer,
        onUploadFoldersFromComputer,
        ui2026: true,
      }),
    [onUploadFilesFromComputer, onUploadFoldersFromComputer],
  );

  return { uploadMenuOptions };
};
